import { ArrowRight, Plane } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const HERO_IMAGE =
  "https://images.pexels.com/photos/18556687/pexels-photo-18556687.jpeg";

// Highlight the ampersand (EN) or the last standalone letter emphasis we want.
// If no ampersand exists, we simply return the title untouched to keep RU clean.
function renderHighlightedTitle(title) {
  const parts = title.split("&");
  if (parts.length < 2) return title;
  return (
    <>
      {parts[0]}
      <span className="text-[#00E5FF]">&</span>
      {parts.slice(1).join("&")}
    </>
  );
}

export const Hero = () => {
  const { t } = useLang();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-16"
    >
      {/* Background layers */}
      <div className="absolute inset-0 -z-30">
        <img
          src={HERO_IMAGE}
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover opacity-40"
          loading="eager"
        />
      </div>
      <div className="absolute inset-0 -z-20 bg-gradient-to-b from-[#0B0F19]/85 via-[#0B0F19]/70 to-[#0B0F19]" />
      <div
        className="absolute inset-0 -z-10 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* Radar glow */}
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-[#00E5FF]/20 blur-[140px] -z-10" />
      <div className="absolute bottom-0 -left-40 w-[520px] h-[520px] rounded-full bg-[#2563EB]/25 blur-[140px] -z-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full">
        <div className="max-w-3xl">
          <div
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-[#00E5FF] px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-[0.18em] mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
            {t.hero.eyebrow}
          </div>

          <h1
            data-testid="hero-title"
            className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            {renderHighlightedTitle(t.hero.title)}
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-8 text-slate-300 text-base sm:text-lg max-w-2xl leading-relaxed"
          >
            {t.hero.subtitle}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              className="group inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-[#0B0F19] font-medium px-6 py-3.5 rounded-sm hover:bg-[#7DF3FF] transition-colors duration-200"
            >
              {t.hero.primaryCta}
              <ArrowRight
                size={18}
                className="transition-transform duration-200 group-hover:translate-x-0.5"
              />
            </a>
            <a
              href="#services"
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/15 text-white font-medium px-6 py-3.5 rounded-sm hover:border-[#00E5FF]/60 hover:text-[#00E5FF] transition-colors duration-200"
            >
              {t.hero.secondaryCta}
            </a>
          </div>

          <dl className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-white/10 pt-8">
            {t.hero.stats.map((stat, i) => (
              <div
                key={i}
                data-testid={`hero-stat-${i}`}
                className="flex flex-col"
              >
                <dt className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight">
                  {stat.value}
                </dt>
                <dd className="mt-1 text-xs uppercase tracking-widest text-slate-400 font-mono">
                  {stat.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>

      {/* corner marker */}
      <div className="absolute bottom-6 right-6 lg:right-10 hidden md:flex items-center gap-2 text-slate-500 font-mono text-xs uppercase tracking-widest">
        <Plane size={14} strokeWidth={1.5} className="text-[#00E5FF]" />
        <span>ATM / CNS</span>
      </div>
    </section>
  );
};

export default Hero;
